import React from 'react';
import Paper from '@material-ui/core/Paper/Paper';
import Table from '@material-ui/core/Table/Table';
import TableBody from '@material-ui/core/TableBody/TableBody';
import TableRow from '@material-ui/core/TableRow/TableRow';
import TableCell from '@material-ui/core/TableCell/TableCell';
import Typography from '@material-ui/core/Typography/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { createStyles, withStyles, WithStyles, Theme } from '@material-ui/core/styles';
import { TableContainer } from "@material-ui/core";

import Title from '../../Common/Title';
import locales from '../../../locales';
import StatisticItem from './StatisticItem';
import classNames from 'classnames';
import SummaryCard from './SummaryCard';

interface Props extends WithStyles<typeof styles> {
  statistic: any;
  moneySymbol: string;
}

const styles = (theme: Theme) => createStyles({
  // Десктопная версия
  desktopPaper: {
    [theme.breakpoints.down(960)]: {
      display: 'none',
    },
  },
  statistics: {
    display: 'flex',
    justifyContent: 'center',
    minHeight: 140,
  },
  statisticItem: {
    padding: '24px 10px',
  },
  tableHeader: {
    fontWeight: 500,
    padding: 24,
  },
  // Мобильная версия
  mobileCard: {
    display: 'none',
    [theme.breakpoints.down(960)]: {
      display: 'block',
    },
  },
  card: {
    borderRadius: 8,
    border: '1px solid #E5E5E5',
    width: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  cardContent: {
    padding: 0,
    width: '100%',
    boxSizing: 'border-box',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: 0,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: 0,
  },
  statsContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
  },
  divider: {
    height: 1,
    backgroundColor: '#E5E5E5',
    margin: 0,
    border: 'none',
    width: '100%',
  },
});

const index: React.FC<Props> = ({ statistic, classes, moneySymbol }) => (
  <>
    {/* Десктопная версия (таблица) */}
    <Paper elevation={3} className={classes.desktopPaper}>
      <Title text={locales.t('statistics.table.title')} borderBottom />
      <div className={classes.statistics}>
        <TableContainer>
          <Table>
            <TableBody>
              <TableRow key="1">
                <TableCell
                  className={classes.tableHeader}
                  component="th"
                  scope="row"
                >
                  { locales.t('statistics.table.guestsRow.title') }
                </TableCell>
                <TableCell className={classes.statisticItem}>
                  <StatisticItem
                    type={locales.t('statistics.table.guestsRow.profit')}
                    stat={statistic.totalReceiptAmount.toFixed()}
                    moneySymbol={moneySymbol}
                    color="primary"
                  />
                </TableCell>
                <TableCell className={classes.statisticItem}>
                  <StatisticItem
                    type={locales.t('statistics.table.guestsRow.check')}
                    stat={statistic.averageReceiptAmount.toFixed()}
                    moneySymbol={moneySymbol}
                  />
                </TableCell>
                <TableCell className={classes.statisticItem}>
                  <StatisticItem
                    type={locales.t('statistics.table.guestsRow.guests')}
                    stat={statistic.totalGuests}
                  />
                </TableCell>
                <TableCell className={classes.statisticItem}>
                  <StatisticItem
                    type={locales.t('statistics.table.guestsRow.newGuests')}
                    stat={statistic.totalNewGuests}
                  />
                </TableCell>
                <TableCell style={{ flex: 1 }} />
              </TableRow>
              <TableRow key="2">
                <TableCell
                  className={classes.tableHeader}
                  component="th"
                  scope="row"
                >
                  { locales.t('statistics.table.reserveRow.title') }
                </TableCell>
                <TableCell className={classes.statisticItem}>
                  <StatisticItem
                    type={locales.t('statistics.table.reserveRow.all')}
                    stat={statistic.total}
                  />
                </TableCell>
                <TableCell className={classes.statisticItem}>
                  <StatisticItem
                    type={locales.t('statistics.table.reserveRow.success')}
                    color="primary"
                    stat={statistic.totalClosed}
                  />
                </TableCell>
                <TableCell className={classes.statisticItem}>
                  <StatisticItem
                    type={locales.t('statistics.table.reserveRow.canceledByRestaurant')}
                    stat={statistic.totalCancelledByRestaurant}
                    color="error"
                  />
                </TableCell>
                <TableCell className={classes.statisticItem}>
                  <StatisticItem
                    type={locales.t('statistics.table.reserveRow.rejectByRestaurant')}
                    stat={statistic.totalDeclined}
                  />
                </TableCell>
                <TableCell className={classes.statisticItem}>
                  <StatisticItem
                    type={locales.t('statistics.table.reserveRow.canceledByGuest')}
                    stat={statistic.totalCancelledByGuest}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </Paper>

    {/* Мобильная версия (карточки) */}
    <Card className={classNames(classes.mobileCard)} elevation={0}>
      <CardContent className={classes.cardContent}>
        <Typography className={classes.title}>
          {locales.t('statistics.table.title')}
        </Typography>

        <SummaryCard 
          badge='Гости' 
          items={
            [
              {
                title: "Заработано с GuestMe",
                value: statistic.totalReceiptAmount.toFixed(),
                moneySymbol,
              },
              {
                title: "Средний чек",
                value: statistic.averageReceiptAmount.toFixed(),
                moneySymbol,
              },
              {
                title: "Всего гостей",
                value: statistic.totalGuests
              },
              {
                title: "Новых гостей",
                value: statistic.totalNewGuests
              },
            ]
          } 
        />

        <SummaryCard 
          badge='Резервы' 
          items={
            [
              {
                title: "Всего",
                value: statistic.total
              },
              {
                title: "Успешных",
                value: statistic.totalClosed
              },
              {
                title: "Отменено рестораном",
                value: statistic.totalCancelledByRestaurant,
                isError: true
              },
              {
                title: "Отклонено рестораном",
                value: statistic.totalDeclined
              },
              {
                title: "Отменено гостем",
                value: statistic.totalCancelledByGuest
              },
            ]
          } 
        />
      </CardContent>
    </Card>
  </>
);


export default withStyles(styles)(index);
